import React from "react";
import dayjs from "dayjs";
import { Edit } from "@refinedev/antd";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { message, Form } from "antd";
import {
  PURCHASE_ORDER_BY_ID_QUERY,
  UPDATE_PURCHASE_ORDER_MUTATION,
  SUBMIT_PURCHASE_ORDER_MUTATION,
} from "./queries";
import { PurchaseOrderForm } from "./components/PurchaseOrderForm";
import { useParams } from "react-router-dom";
import { PurchaseOrder } from "./interface";

export const EditPurchaseOrder: React.FC = () => {
  const params = useParams();
  const id = params?.id as string;

  const [form] = Form.useForm();

  // Fetch one PO
  const { data, isLoading, refetch } = useCustom<any>({
    url: "",
    method: "post",
    meta: {
      gqlQuery: PURCHASE_ORDER_BY_ID_QUERY,
      variables: { id },
    },
    queryOptions: { enabled: !!id },
  });

  const record: PurchaseOrder | undefined = (data as any)?.data?.purchaseOrder ?? (data as any)?.data ?? undefined;

  // Update draft
  const { mutate: updateMutate, isLoading: updating } = useCustomMutation();
  // Submit (existing order path)
  const { mutate: submitMutate, isLoading: submitting } = useCustomMutation();

  const onSaveDraft = async (values: any) => {
    if (!id) return;
    updateMutate(
      {
        url: "",
        method: "post",
        values: {
          id,
          input: values,
        },
        meta: { gqlMutation: UPDATE_PURCHASE_ORDER_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Purchase Order updated");
          refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to update order");
        },
      },
    );
  };

  const onSubmit = async (payload: { orderId?: string; orderData?: any }) => {
    if (!id) return;
    submitMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            orderId: id,
            orderData: payload.orderData,
          },
        },
        meta: { gqlMutation: SUBMIT_PURCHASE_ORDER_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Purchase Order submitted");
          refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to submit order");
        },
      },
    );
  };

  return (
    <Edit
      title="Edit Purchase Order"
      isLoading={isLoading}
      saveButtonProps={{ loading: updating || submitting, style: { display: "none" } }}
      footerButtons={() => null}
    >
      <PurchaseOrderForm
        form={form}
        isEdit
        orderId={id}
        initialValues={{
          orderNo: record?.orderNo,
          templateId: record?.templateId,
          teamId: record?.teamId,
          dateCreated: record?.dateCreated ? dayjs(record.dateCreated) : undefined,
          orderDeadline: record?.orderDeadline ? dayjs(record.orderDeadline) : undefined,
          expectedDeliveryDate: record?.expectedDeliveryDate ? dayjs(record.expectedDeliveryDate) : undefined,
          orderedBy: record?.orderedBy,
          vendor: record?.vendor?.vendorId, // Pass vendor ID for Select
          purchaseRequestRef: record?.purchaseRequestRef,
          items: record?.items,
          subtotalAmount: record?.subtotalAmount,
          taxAmount: record?.taxAmount,
          shippingAmount: record?.shippingAmount,
          discountAmount: record?.discountAmount,
          totalAmount: record?.totalAmount,
          currency: record?.currency,
          exchangeRate: record?.exchangeRate,
          paymentTerms: record?.paymentTerms,
          shippingInfo: record?.shippingInfo,
          status: record?.status,
          approvals: record?.approvals,
          budgetValidation: record?.budgetValidation,
          attachments: record?.attachments,
          notes: record?.notes,
          termsAndConditions: record?.termsAndConditions,
          tags: record?.tags,
        }}
        onSaveDraft={onSaveDraft}
        onSubmit={onSubmit as any}
      />
    </Edit>
  );
};